import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { buildProviderPath } from '~/components/Admin/IntegrationPage/util';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { CopyButton } from '~/eds/src/components/Button/subcomponents/CopyButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { api } from '~/redux/api/createApi';

import { InfoDataType, SyncPairConfigInfo } from './types';

export const useConfigurationInfos = ({
  id: syncPairId,
  providerOptions,
  createdBy,
  evisortFolderPath,
  providerFolderPath,
  providerPrefix,
  allowedSyncFileTypes,
  name,
  dateCreated,
  dateUpdated,
  provider,
  skipEmptyFolders,
}: SyncPairConfigInfo) => {
  const clientId = useClientId();
  const intl = useIntl();
  const enableSkipEmptyFolders = useFlag(FlagType.SkipEmptyFolders);

  const { data: userCreate } = api.endpoints.resolveUser.useQuery({
    id: createdBy,
    params: { clientId, includeDeleted: true },
  });

  const { data: userUpdate } = api.endpoints.resolveUser.useQuery({
    id: providerOptions.updatedBy ?? createdBy,
    params: { clientId, includeDeleted: true },
  });

  const userCreateInfo = useMemo(() => {
    if (!userCreate) {
      return {
        firstName: '',
        lastName: '',
        email: '',
        id: '',
      };
    }

    const { firstName, lastName, email, id } = userCreate;
    return { firstName, lastName, email, id };
  }, [userCreate]);

  const userUpdateInfo = useMemo(() => {
    if (!userUpdate) {
      return userCreateInfo;
    }
    const { firstName, lastName, email, id } = userUpdate;
    return { firstName, lastName, email, id };
  }, [userUpdate]);

  const chips = allowedSyncFileTypes.map((fileType: string) => ({
    text: fileType,
  }));

  const hasSkipEmptyFolders = enableSkipEmptyFolders
    ? [
        {
          title: intl.formatMessage({
            id: ' StorageUseConfigurationInfos.skipEmptyFolders',
            defaultMessage: 'Skip Empty Folders',
            description: 'Title for skip empty folders section',
          }),
          type: InfoDataType.TEXT,
          props: {
            text:
              skipEmptyFolders === true
                ? 'Yes'
                : skipEmptyFolders === false
                  ? 'No'
                  : '-',
          },
        },
      ]
    : [];

  const infos = useMemo(() => {
    return [
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.provider',
          defaultMessage: 'Type',
          description: 'Title for provider section',
        }),
        type: InfoDataType.CONTENT,
        props: {
          children: (
            <Layout spacing={2} align="center">
              <ProviderLogo provider={provider} size="s" />
              <Text>{name}</Text>
            </Layout>
          ),
        },
      },
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.evisortFolder',
          defaultMessage: 'Evisort Folder',
          description: 'Title for Evisort folder section',
        }),
        type: InfoDataType.FILESYSTEM,
        props: {
          folder: {
            name: evisortFolderPath,
            type: 'internal',
            isConcise: true,
            path: evisortFolderPath,
          },
        },
      },
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.externalFolder',
          defaultMessage: 'External Folder',
          description: 'Title for External folder section',
        }),
        type: InfoDataType.FILESYSTEM,
        props: {
          folder: {
            name,
            type: 'external',
            isConcise: true,
            path:
              providerFolderPath === '/'
                ? name
                : buildProviderPath(providerFolderPath, providerPrefix),
          },
        },
      },
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.syncPairId',
          defaultMessage: 'Sync Pair ID',
          description: 'Title for sync pair ID section',
        }),
        type: InfoDataType.CONTENT,
        props: {
          children: (
            <Layout spacing={2} align="center">
              <Text>{syncPairId}</Text>
              <CopyButton copyText={syncPairId} size="s" />
            </Layout>
          ),
        },
      },
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.fileTypes',
          defaultMessage: 'File Types',
          description: 'Title for file types section',
        }),
        type: InfoDataType.CHIPS,
        props: {
          chips,
        },
      },
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.dateCreated3',
          defaultMessage: 'Date Created',
          description: 'Title for date created section',
        }),
        type: InfoDataType.USER_ACTIVITY,
        props: {
          activity: intl.formatMessage({
            id: 'StorageUseConfigurationInfos.dateCreated4',
            defaultMessage: 'Date Created',
            description: 'Activity name for date created',
          }),
          date: new Date(dateCreated),
          user: {
            id: userCreateInfo.id,
            firstName: userCreateInfo.firstName,
            lastName: userCreateInfo.lastName,
            email: userCreateInfo.email,
          },
        },
        format: 'short_datetime',
      },
      {
        title: intl.formatMessage({
          id: 'StorageUseConfigurationInfos.dateUpdated',
          defaultMessage: 'Date Updated',
          description: 'Title for date updated section',
        }),
        type: InfoDataType.USER_ACTIVITY,
        props: {
          activity: intl.formatMessage({
            id: 'StorageUseConfigurationInfos.Updated',
            defaultMessage: 'Date Updated',
            description: 'Activity name for date updated',
          }),
          date: new Date(dateUpdated),
          user: {
            id: userCreateInfo.id,
            firstName: userCreateInfo.firstName,
            lastName: userCreateInfo.lastName,
            email: userCreateInfo.email,
          },
        },
        format: 'short_datetime',
      },
      ...hasSkipEmptyFolders,
    ];
  }, [
    providerOptions,
    createdBy,
    evisortFolderPath,
    allowedSyncFileTypes,
    dateCreated,
    dateUpdated,
    userCreateInfo,
    userUpdateInfo,
  ]);

  return infos;
};
